import { useState } from 'react';
import {
  joinWaitlist,
  useCurrentUserProfile,
  useHasVotedForPlatform,
  useWaitlistCount,
} from '@busy/api';
import { ModalCloseButton, ModalShell, useToast } from '@busy/ui';
import { useT } from '../lib/i18n';

// Vote / get-notified modal for booking platform integrations.
// Mirrors demo's openWaitlist (DEMO_REFERENCE.html:16818). Two states:
//   - User hasn't voted yet → email + feature dropdown + Join waitlist button
//   - User already voted     → "You're already on the list" callout + Got it
//
// Used by Onboarding step 3 + Settings → Integrations.
export function WaitlistModal({
  platform,
  onClose,
}: {
  platform: string;
  onClose: () => void;
}) {
  const t = useT();
  const toast = useToast();
  const profile = useCurrentUserProfile();
  const { voted } = useHasVotedForPlatform(platform);
  const { data: count } = useWaitlistCount(platform);

  const [email, setEmail] = useState(profile.data?.email ?? '');
  const [feature, setFeature] = useState('all');
  const [submitting, setSubmitting] = useState(false);

  async function handleJoin() {
    if (!email || !email.includes('@')) {
      toast(t('waitlist.invalidEmail'), 'error');
      return;
    }
    setSubmitting(true);
    const { error } = await joinWaitlist(platform, email, feature);
    setSubmitting(false);
    if (error) {
      toast(t('waitlist.error', { message: error.message }), 'error');
      return;
    }
    onClose();
    toast(t('waitlist.joinedToast', { platform }), 'success');
  }

  // 42 floor (matches the demo's synthetic count) + real DB count so
  // early visitors see meaningful social proof.
  const displayCount = (count ?? 0) + 42;

  return (
    <ModalShell open onClose={onClose} size="sm" ariaLabel={t('waitlist.aria')}>
      <div className="bg-navy-900 text-white px-6 pt-7 pb-5 rounded-t-2xl relative">
        <ModalCloseButton onClose={onClose} variant="dark" />
        <div className="text-[11px] tracking-[0.16em] uppercase font-bold text-gold-400 mb-1.5">
          {t('waitlist.eyebrow', { platform: platform.toUpperCase() })}
        </div>
        <h2 className="font-display text-2xl leading-tight">{t('waitlist.title')}</h2>
        <p className="text-sm text-white/72 mt-2 leading-relaxed">
          {voted
            ? t('waitlist.subAlready', { platform })
            : t('waitlist.subNew', { platform })}
        </p>
      </div>

      <div className="p-6 space-y-4">
        {voted ? (
          <div className="rounded-xl border border-green-200 dark:border-green-600/40 bg-green-50 dark:bg-green-600/15 p-4 flex items-start gap-3">
            <CheckIcon className="w-5 h-5 text-green-600 shrink-0 mt-0.5" />
            <p className="text-sm text-green-900 dark:text-green-100 leading-relaxed">
              <strong>{t('waitlist.onListTitle')}</strong> {t('waitlist.onListSub', { platform })}
            </p>
          </div>
        ) : (
          <>
            <div>
              <label className="block text-xs font-bold text-ink-500 mb-1.5">
                {t('waitlist.email')}
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('waitlist.emailPh')}
                className="w-full h-11 px-3 rounded-xl border-[1.5px] border-line-strong bg-surface text-ink-900 placeholder:text-ink-500 focus:outline-none focus:border-navy-700 dark:focus:border-gold-500"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-ink-500 mb-1.5">
                {t('waitlist.feature')}
              </label>
              <select
                value={feature}
                onChange={(e) => setFeature(e.target.value)}
                className="w-full h-11 px-3 rounded-xl border-[1.5px] border-line-strong bg-surface text-ink-900 focus:outline-none focus:border-navy-700 dark:focus:border-gold-500"
              >
                <option value="clients">{t('waitlist.feat.clients')}</option>
                <option value="appts">{t('waitlist.feat.appts')}</option>
                <option value="revenue">{t('waitlist.feat.revenue')}</option>
                <option value="services">{t('waitlist.feat.services')}</option>
                <option value="all">{t('waitlist.feat.all')}</option>
              </select>
            </div>
            <p className="text-xs text-ink-500 leading-relaxed pt-1">
              {t('waitlist.waiting', { count: displayCount, platform })}
            </p>
          </>
        )}
      </div>

      <div className="border-t border-line bg-bg-alt p-4 flex gap-2">
        {voted ? (
          <button
            type="button"
            onClick={onClose}
            className="flex-1 h-11 rounded-lg bg-navy-900 text-white dark:bg-gold-500 dark:text-navy-900 font-bold"
          >
            {t('waitlist.gotIt')}
          </button>
        ) : (
          <>
            <button
              type="button"
              onClick={handleJoin}
              disabled={submitting}
              className="flex-1 h-11 rounded-lg bg-navy-900 text-white dark:bg-gold-500 dark:text-navy-900 font-bold disabled:opacity-60"
            >
              {submitting ? t('waitlist.joining') : t('waitlist.join')}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-5 h-11 rounded-lg border border-line bg-surface font-semibold text-ink-700"
            >
              {t('waitlist.maybeLater')}
            </button>
          </>
        )}
      </div>
    </ModalShell>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}
